import {uniqBy} from '@shared/helpers/array'
import {eq, gtZero, isEmpty} from '@shared/helpers/lang'
import {mapValueKeys, reduceValueKeys, someValueKeys} from '@shared/helpers/object'

//  TODO:(순환참조) actions에 있는 타입 참조하는 util 함수를 sliceMaker로 이동하면 순환참조가 해결됨
import {DiscountItem} from '@web/actions/productCoupon'
import {DiscountCoupon, DiscountCouponType, DiscountPolicyType, MultiPurchaseUnitType} from '@web/core/constants'
import {isNaverShopping} from '@web/helpers/userAgent'

import {
    CouponView as OrderSheetCoupon,
    DiscountItem as OrderSheetDiscountItem,
    ImmediateCouponView as OrderSheetImmediateCoupon,
    CouponView,
    Discount,
    MultiPurchaseDiscountItem,
} from '../interfaces'

const ProductCouponTypes: DiscountCouponType[] = [
    DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT,
    DiscountCoupon.SELLER_PRODUCT_DISCOUNT,
    DiscountCoupon.MANAGER_IMMEDIATE_DISCOUNT,
    DiscountCoupon.MANAGER_PRODUCT_DISCOUNT,
    DiscountCoupon.SUBSCRIBER_DISCOUNT,
    DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT,
    DiscountCoupon.MULTI_PURCHASE_DISCOUNT,
]

const ProductCouponWithOutTypes: DiscountCouponType[] = ProductCouponTypes.filter(
    (couponType) => couponType !== DiscountCoupon.MULTI_PURCHASE_DISCOUNT,
)

/**
 * 상품당 하나의 쿠폰만 적용이 가능한 쿠폰의 비활성 여부를 구합니다.
 * - 하나의 아이템에 먼저 쿠폰이 사용된 경우, 다른 아이템의 동일한 쿠폰들은 비활성화됩니다.
 * - 만약 상품 즉시 할인이 선택되어 있다면, 스토어 찜할인은 비활성화 됩니다
 */
export function isDisabled({
    coupon,
    selectedCoupon,
    orderSheetItemId,
}: {
    coupon: CouponView
    selectedCoupon: {[orderSheetItemId: string]: CouponView[]}
    orderSheetItemId: string | number
}) {
    return (
        coupon.disabled ||
        // 만약 상품 즉시 할인이 선택되어 있다면, 스토어 찜할인은 비활성화 됩니다
        (coupon.couponType === DiscountCoupon.SUBSCRIBER_DISCOUNT &&
            (selectedCoupon[orderSheetItemId] ?? []).some((selected) => {
                return selected.couponType === DiscountCoupon.SELLER_PRODUCT_DISCOUNT
            })) ||
        // 하나의 아이템에 먼저 쿠폰이 사용된 경우, 다른 아이템의 동일한 쿠폰들은 비활성화됩니다.
        someValueKeys((selectedCoupons, _orderSheetItemId) => {
            return (
                _orderSheetItemId !== orderSheetItemId &&
                selectedCoupons.some(({couponId}) => couponId === coupon.couponId)
            )
        }, selectedCoupon)
    )
}

export const getProductCouponInfo = ({
    coupons,
    selectedCoupons,
}: {
    coupons: {[OrderSheetItemId: string]: CouponView[]}
    selectedCoupons: {[OrderSheetItemId: string]: CouponView[]}
}) => {
    return {
        coupons: mapValueKeys((couponsByItemId, orderSheetItemId) => {
            return couponsByItemId.map((coupon) => {
                return {
                    ...coupon,
                    disabled: isDisabled({
                        coupon,
                        selectedCoupon: selectedCoupons,
                        orderSheetItemId,
                    }),
                }
            })
        }, coupons),
        selectedCoupons,
    }
}

export const getDuplicatedCouponInfo = ({
    coupons,
    selectedCoupons,
}: {
    coupons: {[orderSheetItemId: string]: CouponView[]}
    selectedCoupons: {[orderSheetItemId: string]: CouponView[]}
}) => {
    return mapValueKeys((couponsByItemId, orderSheetItemId) => {
        return couponsByItemId.map((coupon) => {
            return {
                ...coupon,
                disabled: isDisabled({
                    coupon,
                    selectedCoupon: selectedCoupons,
                    orderSheetItemId,
                }),
            }
        })
    }, coupons)
}

export const getProductDiscountInfo = ({
    productItem,
    multiPurchaseDiscounts,
    multiPurchaseDiscountItemIds = [],
}: {
    /** 현재 적용된 할인 정보 */
    productItem: Discount['productItem']
    /** 보유한 복수구매할인 정보 */
    multiPurchaseDiscounts: Record<string, MultiPurchaseDiscountItem>
    /** 복수구매할인을 적용가능한 ids */
    multiPurchaseDiscountItemIds: string[]
}) => {
    /** 상품별 적용된 스토어 할인 금액 (orderSheetItemId 별로 스플릿되어 적용) */
    const storeDiscountAmountByItemId = mapValueKeys((discount) => {
        return discount.reduce((acc, {discountAmount, couponType}) => {
            if (couponType === DiscountCoupon.STORE_DISCOUNT) {
                return acc + discountAmount
            }
            return acc
        }, 0)
    }, productItem)

    /** 상품별 적용된 할인 금액 (스토어 할인 포함) */
    const discountAmountByItemId = mapValueKeys((discount, orderSheetItemId) => {
        return discount.reduce((acc, {discountAmount, discountType}) => {
            if (
                eq(discountType, DiscountCoupon.MULTI_PURCHASE_DISCOUNT) &&
                !multiPurchaseDiscountItemIds.includes(String(orderSheetItemId))
            ) {
                return acc
            }
            return acc + discountAmount
        }, 0)
    }, productItem)

    /** 상품별 적용된 상품 할인 금액 (스토어 할인 제외) */
    const productCouponDiscountAmountByItemId = mapValueKeys((discount) => {
        return discount.reduce((acc, {discountAmount, couponType}) => {
            if (ProductCouponTypes.includes(couponType)) {
                return acc + discountAmount
            }
            return acc
        }, 0)
    }, productItem)

    /** 상품별 적용된 복수구매 할인 금액 */
    const multiPurchaseDiscountAmountByItemId = mapValueKeys((discount) => {
        return discount.reduce((acc, {discountAmount, couponType}) => {
            if (couponType === DiscountCoupon.MULTI_PURCHASE_DISCOUNT) {
                return acc + discountAmount
            }
            return acc
        }, 0)
    }, productItem)
    /** 복수금액 할인 관련 정보 */
    const multiPurchaseInfo = {
        /** 상품별 적용된 복수구매 할인 금액 */
        discountAmountByItemId: multiPurchaseDiscountAmountByItemId,

        /** 상품별 적용된 복수금액 제외된 상품 할인 금액 */
        productDiscountAmountByItemId: mapValueKeys((discount) => {
            return discount.reduce((acc, {couponType, discountAmount}) => {
                return ProductCouponWithOutTypes.includes(couponType) ? acc + discountAmount : acc
            }, 0)
        }, productItem),

        discountInfo: mapValueKeys<
            Record<string, MultiPurchaseDiscountItem>,
            {
                discountUnitType?: DiscountPolicyType
                discountValue?: number
                validityPeriodEndDateTime?: string
                multiPurchaseMinOrderValue?: number
                multiPurchaseUnitType?: MultiPurchaseUnitType
            }
        >((discount) => {
            return {
                discountUnitType: discount?.discountUnitType,
                discountValue: discount?.discountValue,
                multiPurchaseMinOrderValue: discount?.multiPurchaseMinOrderValue,
                multiPurchaseUnitType: discount?.multiPurchaseUnitType,
            }
        }, multiPurchaseDiscounts),

        /** 복수구매 할인 적용된 상품 ID */
        multiPurchaseDiscountSelectedOrderSheetItemIds: reduceValueKeys<
            string[],
            typeof multiPurchaseDiscountAmountByItemId
        >(
            (acc, discountAmount, orderSheetItemId) => {
                if (gtZero(discountAmount)) {
                    acc.push(String(orderSheetItemId))
                }
                return acc
            },
            [],
            multiPurchaseDiscountAmountByItemId,
        ),
    }

    /** 상품별 적용된 할인 금액 */
    const discountAmount = reduceValueKeys((acc, amount) => acc + amount, 0, discountAmountByItemId)

    return {
        discountAmountByItemId,
        productCouponDiscountAmountByItemId,
        multiPurchaseInfo,
        discountAmount,
        storeDiscountAmountByItemId,
    }
}

export const getDeliveryCouponInfo = ({
    coupons,
    selectedCoupons,
}: {
    coupons: {[DeliveryGroupKey: string]: CouponView[]}
    selectedCoupons: {[DeliveryGroupKey: string]: Maybe<CouponView>}
}) => {
    return {
        coupons: mapValueKeys((couponsByDeliveryGroup, deliveryGroupKey) => {
            return couponsByDeliveryGroup.map((coupon) => {
                return {
                    ...coupon,
                    disabled: (function () {
                        return (
                            coupon.disabled ||
                            // 하나의 아이템에 먼저 쿠폰이 사용된 경우, 다른 아이템의 동일한 쿠폰들은 비활성화됩니다.
                            someValueKeys((selectedCoupon, _deliveryGroupKey) => {
                                return !!(
                                    _deliveryGroupKey !== deliveryGroupKey &&
                                    selectedCoupon &&
                                    selectedCoupon.couponId === coupon.couponId
                                )
                            }, selectedCoupons)
                        )
                    })(),
                }
            })
        }, coupons),
        selectedCoupons: mapValueKeys((coupon, deliveryGroupKey) => {
            if (coupons[deliveryGroupKey] && coupon) {
                // 배송비 쿠폰이 재조회 되었을때, 선택된 쿠폰과 정보가 다를수 있음 선택된 쿠폰 정보로 노출
                return coupons[deliveryGroupKey].find(({couponId}) => couponId === coupon.couponId) ?? null
            }
            return null
        }, selectedCoupons),
    }
}

export const getStoreCouponInfo = ({
    coupons,
    selectedCoupons,
}: {
    coupons: {[MerchantNo: number]: CouponView[]}
    selectedCoupons: {[MerchantNo: number]: Maybe<CouponView>}
}) => {
    return {
        coupons: mapValueKeys((couponsByMerchant, merchantNo) => {
            return couponsByMerchant.map((coupon) => {
                return {
                    ...coupon,
                    disabled: (function () {
                        return (
                            coupon.disabled ||
                            // 하나의 아이템에 먼저 쿠폰이 사용된 경우, 다른 아이템의 동일한 쿠폰들은 비활성화됩니다.
                            someValueKeys((selectedCoupon, _merchantNo) => {
                                return !!(
                                    _merchantNo !== merchantNo &&
                                    selectedCoupon &&
                                    selectedCoupon.couponId === coupon.couponId
                                )
                            }, selectedCoupons)
                        )
                    })(),
                }
            })
        }, coupons),
        selectedCoupons,
    }
}

export const isDiscountOverFixedCoupon = (sellerExceptionCode: string) =>
    sellerExceptionCode === 'DISCOUNT_OVER_FIXED_TRADITIONAL_ALCOHOL_RATE' ||
    sellerExceptionCode === 'DISCOUNT_OVER_FIXED_BOOK_RATE'

const getProductCouponType = (
    coupon: Pick<CouponView, 'couponProviderType' | 'couponDiscountType'>,
): DiscountCouponType => {
    const isSeller = coupon.couponProviderType === 'SELLER'
    switch (coupon.couponDiscountType) {
        case 'SELLER_IMMEDIATE_DISCOUNT':
            return DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT
        case 'MULTI_PURCHASE_DISCOUNT':
            return DiscountCoupon.MULTI_PURCHASE_DISCOUNT
        case 'PRODUCT_DISCOUNT':
            return isSeller ? DiscountCoupon.SELLER_PRODUCT_DISCOUNT : DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        /**
         * MANAGER_PRODUCT_DISCOUNT / MERCHANT_PRODUCT_DISCOUNT 는
         * C1 에서만 내려오므로 고정하여 관리
         */
        case 'MANAGER_PRODUCT_DISCOUNT':
            return DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        case 'MERCHANT_PRODUCT_DISCOUNT':
            return DiscountCoupon.SELLER_PRODUCT_DISCOUNT
        case 'DUPLICATE_PRODUCT_DISCOUNT':
            return DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
        case 'STORE_DISCOUNT':
            return DiscountCoupon.STORE_DISCOUNT
    }
    return DiscountCoupon.ORDER_DISCOUNT
}

export const convertDiscountInfo = (discountItem: DiscountItem): OrderSheetDiscountItem => {
    return {
        ...discountItem,
        validityPeriodEndDateTime: discountItem.validityPeriodEndDateTime ?? undefined,
        multiPurchaseMinOrderValue: discountItem.multiPurchaseMinOrderValue ?? undefined,
        multiPurchaseUnitType: discountItem.multiPurchaseUnitType ?? undefined,
        couponType: getProductCouponType({
            couponDiscountType: discountItem.discountType,
            couponProviderType: discountItem.discountProviderType,
        }),
    }
}

export function filterProductCoupon<S, C>({
    selectedCoupons,
    couponsByOrderSheetItemId,
    someFunction,
}: {
    selectedCoupons: {[OrderSheetItemId: string]: S[]}
    couponsByOrderSheetItemId: {[OrderSheetItemId: string]: C[]}
    someFunction: (coupon: C, selectedCoupon: S) => boolean
}) {
    return mapValueKeys((coupons, orderSheetItemId) => {
        return coupons.filter((selectedCoupon) => {
            return couponsByOrderSheetItemId[orderSheetItemId]?.some((coupon) => someFunction(coupon, selectedCoupon))
        })
    }, selectedCoupons)
}

export function eqDiscountItem(discount: DiscountItem, selectedCoupon: OrderSheetCoupon): boolean {
    return (
        selectedCoupon.couponDiscountType === discount.discountType &&
        selectedCoupon.couponProviderType === discount.discountProviderType
    )
}
export function eqDiscountItemByImmediateCoupon(
    discount: DiscountItem,
    selectedCoupon: OrderSheetImmediateCoupon,
): boolean {
    return (
        selectedCoupon.discountType === discount.discountType &&
        selectedCoupon.discountProviderType === discount.discountProviderType
    )
}

/** 배송비쿠폰을 flat하게 정렬 */
export const getDeliveryCoupons = ({
    couponsByDeliveryGroupKey,
}: {
    couponsByDeliveryGroupKey: Record<string, CouponView[]>
}) => {
    return uniqBy(
        (deliveryCoupon) => deliveryCoupon.couponId,
        reduceValueKeys<OrderSheetCoupon[], Record<string, OrderSheetCoupon[]>>(
            (acc, coupons) => [...acc, ...coupons],
            [],
            couponsByDeliveryGroupKey,
        ),
    )
}

/**
 * C2는 나할 과제부터 배송비 쿠폰 최적 계산되어 내려온다, FE에서 직접 계산할 필요 X
 * 앱구분에 따라서 적용 가능한 쿠폰만 최적계산에 포함된다.
 */
export const getOptimizedDeliveryCoupon = ({
    couponsByDeliveryGroupKey,
    deliveryGroupsByKey,
    deliveryCoupons,
}: {
    couponsByDeliveryGroupKey: {
        [DeliveryGroupKey: string]: OrderSheetCoupon[]
    }
    deliveryGroupsByKey: {
        [deliveryGroupKey: string]: {
            deliveryFee: number
            additionalDeliveryFee: number
        }
    }
    deliveryCoupons: OrderSheetCoupon[]
}): Maybe<{[DeliveryGroupKey: string]: Maybe<OrderSheetCoupon>}> => {
    /** 적용가능한 배송비 쿠폰이 있는 배송묶음 그룹 */
    let couponGroupCount = 0

    /** 모든 쿠폰이 쇼핑앱전용 쿠폰인데, 쇼핑앱환경이 아닌경우 early return  */
    if (deliveryCoupons.every(({locationType}) => locationType === 'SHOPPING_APP') && !isNaverShopping) {
        return null
    }

    /** 쿠폰 목록에는 존재해야하지만, 최적화 계산에서는 빠져야 하는 경우 로직 추가 */
    const filteredCouponsByDeliveryGroupKey = mapValueKeys((coupons) => {
        return coupons.filter((coupon) => {
            return coupon.locationType !== 'SHOPPING_APP' || isNaverShopping
        })
    }, couponsByDeliveryGroupKey)

    /** 배송비묶음 1개인 Case */
    const singleDeliveryGroupsResult: {[DeliveryGroupKey: string]: Maybe<OrderSheetCoupon>} = mapValueKeys(
        (coupons, deliveryGroupKey) => {
            if (isEmpty(coupons)) {
                return null
            }

            couponGroupCount++
            const deliveryGroup = deliveryGroupsByKey[deliveryGroupKey]
            /**
             * 할인금액 높은순
             * 쿠폰발급번호 (couponPublicationId) 낮은 순
             * 쿠폰번호 (couponId) 낮은 순
             */
            coupons.sort((a, b) => {
                if (a.discountValue > b.discountValue) {
                    return -1
                } else if (a.discountValue < b.discountValue) {
                    return 1
                } else if (a.couponPublicationId < b.couponPublicationId) {
                    return -1
                } else if (a.couponPublicationId > b.couponPublicationId) {
                    return 1
                } else if (a.couponId < b.couponId) {
                    return -1
                } else if (a.couponId > b.couponId) {
                    return 1
                }
                return 0
            })

            const feeByGroup = (deliveryGroup?.deliveryFee ?? 0) + (deliveryGroup?.additionalDeliveryFee ?? 0)

            const selectedCoupon = coupons.reduce((acc, coupon) => {
                /**
                 * 1. 쿠폰은 할인가격이 높은 순으로 정렬되어 있다.
                 * 2. 쿠폰금액이 배송비보다 크거나 같을 경우엔 절대값비교로 덜 손해보는 쪽 선택
                 * 3. 쿠폰금액이 배송비보다 작을 땐 더 할인이 되는 쪽 선택
                 */
                if (
                    feeByGroup - coupon.discountValue <= 0
                        ? Math.abs(feeByGroup - coupon.discountValue) < Math.abs(feeByGroup - (acc?.discountValue ?? 0))
                        : feeByGroup - coupon.discountValue < feeByGroup - (acc?.discountValue ?? 0)
                ) {
                    return coupon
                }
                return acc
            }, coupons[0])

            return selectedCoupon ?? null
        },
        filteredCouponsByDeliveryGroupKey,
    )

    /**
     * 배송비묶음 N개인 Case
     * 1. 각 배송묶음별 배송비를 내림차순으로 > 그룹키를 오름차순으로 정렬
     * 2. 배송묶음에 적용가능한 쿠폰 있을 경우 그 쿠폰이 유일하므로 바로 적용
     **/
    let isCouponSelected = false
    const multiDeliveryGroupResult: {[DeliveryGroupKey: string]: Maybe<OrderSheetCoupon>} = Object.entries(
        deliveryGroupsByKey,
    )
        .sort(([aKey, aValue], [bKey, bValue]) => {
            const aTotalDeliveryFee = aValue.deliveryFee + aValue.additionalDeliveryFee
            const bTotalDeliveryFee = bValue.deliveryFee + bValue.additionalDeliveryFee
            if (aTotalDeliveryFee > bTotalDeliveryFee) {
                return -1
            } else if (aTotalDeliveryFee < bTotalDeliveryFee) {
                return 1
            }
            return aKey < bKey ? -1 : -1
        })
        .reduce((acc, [key]) => {
            if (filteredCouponsByDeliveryGroupKey[key]?.length === 1 && !isCouponSelected) {
                isCouponSelected = true
                return {
                    ...acc,
                    [key]: filteredCouponsByDeliveryGroupKey[key][0],
                }
            }

            return {
                ...acc,
                [key]: null,
            }
        }, {})

    if (couponGroupCount === 1) {
        return singleDeliveryGroupsResult
    } else if (deliveryCoupons?.length === 1) {
        return multiDeliveryGroupResult
    }

    return null
}

export function clearProductCoupon<S>({selected}: {selected: {[OrderSheetItemId: string]: S | S[]}}) {
    return reduceValueKeys(
        (acc, coupons, orderSheetItemId) => {
            return {
                ...acc,
                ...(!isEmpty(coupons) && {
                    [orderSheetItemId]: coupons,
                }),
            }
        },
        {},
        selected,
    )
}
